import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { TOUR_STATE_KEY, TOUR_COMPLETED_KEY, TOUR_TIMING } from "./constants";
import tourSections from "./tourSections";

const EnterpriseTourContext = createContext(null);

// Persist state to sessionStorage so it survives page navigation
const saveState = (state) => {
  sessionStorage.setItem(TOUR_STATE_KEY, JSON.stringify(state));
};

const loadState = () => {
  try {
    const raw = sessionStorage.getItem(TOUR_STATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const clearState = () => {
  sessionStorage.removeItem(TOUR_STATE_KEY);
};

export const EnterpriseTourProvider = ({ children }) => {
  const saved = loadState();

  const [isActive, setIsActive] = useState(saved?.isActive || false);
  const [sectionIndex, setSectionIndex] = useState(saved?.sectionIndex || 0);
  const [stepIndex, setStepIndex] = useState(saved?.stepIndex || 0);
  const [isPaused, setIsPaused] = useState(false);
  const [showCutScene, setShowCutScene] = useState(saved?.showCutScene || false);
  const [isNavigating, setIsNavigating] = useState(false);
  const navigateRef = useRef(null);

  // All steps are always included - no mode filtering
  const getStepsForSection = useCallback((secIdx) => {
    const section = tourSections[secIdx];
    if (!section) return [];
    return section.steps;
  }, []);

  const currentSection = tourSections[sectionIndex] || null;
  const currentSteps = getStepsForSection(sectionIndex);
  const currentStep = currentSteps[stepIndex] || null;

  // Total step count across all sections (for progress bar)
  const totalSteps = tourSections.reduce(
    (acc, sec, idx) => acc + getStepsForSection(idx).length + (sec.cutScene ? 1 : 0),
    0
  );

  const completedSteps = tourSections.slice(0, sectionIndex).reduce(
    (acc, sec, idx) => acc + getStepsForSection(idx).length + (sec.cutScene ? 1 : 0),
    0
  ) + stepIndex + (currentSection?.cutScene && !showCutScene ? 1 : 0);

  const progress = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  // Persist on every state change
  useEffect(() => {
    if (isActive) {
      saveState({ isActive, sectionIndex, stepIndex, showCutScene });
    }
  }, [isActive, sectionIndex, stepIndex, showCutScene]);

  // Set navigate function from the hook (called by useTourNavigation)
  const setNavigateFn = useCallback((fn) => {
    navigateRef.current = fn;
  }, []);

  const startTour = useCallback(() => {
    setSectionIndex(0);
    setStepIndex(0);
    setIsActive(true);
    setIsPaused(false);
    // If first section has a cut scene, show it
    const first = tourSections[0];
    setShowCutScene(!!first?.cutScene);
    localStorage.removeItem(TOUR_COMPLETED_KEY);
  }, []);

  const exitTour = useCallback(() => {
    setIsActive(false);
    setSectionIndex(0);
    setStepIndex(0);
    setShowCutScene(false);
    setIsPaused(false);
    clearState();
    localStorage.setItem(TOUR_COMPLETED_KEY, "true");
  }, []);

  // Navigate to a section's page if needed
  const navigateToSection = useCallback(
    (section, appConfig) => {
      if (!section?.page || !navigateRef.current) return Promise.resolve();

      let targetPage = section.page;

      // Merge cloned prospect's demoTour overrides (from registration) over static config
      const stored = JSON.parse(localStorage.getItem("demoTourOverride") || "null");
      const demoTour = { ...(appConfig?.tenant?.demoTour || {}), ...stored };
      const hasMySlug = targetPage.includes("{myWantAdSlug}");
      const hasOtherSlug = targetPage.includes("{otherWantAdSlug}");
      if ((hasMySlug && !demoTour.myWantAdSlug) || (hasOtherSlug && !demoTour.otherWantAdSlug)) {
        console.warn("[Tour] Skipping section - missing demo data slug for:", targetPage);
        return Promise.resolve("skip");
      }
      targetPage = targetPage.replace("{myWantAdSlug}", demoTour.myWantAdSlug || "");
      targetPage = targetPage.replace("{otherWantAdSlug}", demoTour.otherWantAdSlug || "");

      // Set up localStorage if needed for the page
      if (section.setupLocalStorage) {
        const { key, value } = section.setupLocalStorage;
        if (key === "memberBuy") {
          const existing = JSON.parse(localStorage.getItem("memberBuy") || "{}");
          // Use myWantAdId for sections referencing myWantAdSlug, otherWantAdId otherwise
          const wantAdId = section.page.includes("{myWantAdSlug}")
            ? demoTour.myWantAdId
            : demoTour.otherWantAdId;
          localStorage.setItem(
            "memberBuy",
            JSON.stringify({ ...existing, wantAdId: wantAdId, ...value })
          );
        } else if (key === "memberInventory") {
          localStorage.setItem(
            "memberInventory",
            JSON.stringify({ inventoryId: demoTour.inventoryId, ...value })
          );
        } else if (key === "FilterTabKey") {
          localStorage.setItem("FilterTabKey", JSON.stringify(value));
          // Also set selectedPropertyId so the Match tab pre-selects the tour property
          if (demoTour.inventoryId) {
            localStorage.setItem(
              "selectedPropertyId",
              JSON.stringify({ Inv_Id: demoTour.inventoryId })
            );
          }
        } else {
          localStorage.setItem(key, JSON.stringify(value));
        }
      }

      const currentPath = window.location.pathname + window.location.search;
      const targetPath = targetPage.split("?")[0];
      const targetSearch = targetPage.includes("?") ? "?" + targetPage.split("?")[1] : "";

      if (currentPath.startsWith(targetPath)) {
        // Already on the right page - may need to update search params
        if (targetSearch && !window.location.search.includes(targetSearch.slice(1))) {
          navigateRef.current(targetPage);
        }
        return Promise.resolve();
      }

      setIsNavigating(true);
      navigateRef.current(targetPage);

      return new Promise((resolve) => {
        // Force scroll to top after navigation to prevent auto-scroll
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 100);
        setTimeout(() => {
          setIsNavigating(false);
          resolve();
        }, TOUR_TIMING.NAVIGATION_SETTLE_MS);
      });
    },
    []
  );

  const next = useCallback(
    (appConfig) => {
      const steps = getStepsForSection(sectionIndex);

      // If showing cut scene, move to first step (or next section if no steps)
      if (showCutScene) {
        setShowCutScene(false);
        if (steps.length > 0) {
          setStepIndex(0);
          const section = tourSections[sectionIndex];
          // [US-005] If navigation returns "skip", auto-advance to next section
          navigateToSection(section, appConfig).then((result) => {
            if (result === "skip") next(appConfig);
          });
        } else {
          // Section has only a cut scene (like data-strategy), move to next section
          const nextSec = sectionIndex + 1;
          if (nextSec >= tourSections.length) {
            exitTour();
            return;
          }
          setSectionIndex(nextSec);
          setStepIndex(0);
          setShowCutScene(!!tourSections[nextSec]?.cutScene);
          if (!tourSections[nextSec]?.cutScene) {
            navigateToSection(tourSections[nextSec], appConfig);
          }
        }
        return;
      }

      const nextStep = stepIndex + 1;
      if (nextStep < steps.length) {
        setStepIndex(nextStep);
        return;
      }

      // Move to next section
      const nextSec = sectionIndex + 1;
      if (nextSec >= tourSections.length) {
        exitTour();
        return;
      }

      setSectionIndex(nextSec);
      setStepIndex(0);
      const nextSection = tourSections[nextSec];
      if (nextSection?.cutScene) {
        // Navigate to the section's page before showing cut scene (e.g. return to /home for final)
        if (nextSection.page) {
          navigateToSection(nextSection, appConfig).then(() => {
            setShowCutScene(true);
          });
        } else {
          setShowCutScene(true);
        }
      } else {
        setShowCutScene(false);
        // [US-005] If navigation returns "skip", auto-advance again
        navigateToSection(nextSection, appConfig).then((result) => {
          if (result === "skip") next(appConfig);
        });
      }
    },
    [sectionIndex, stepIndex, showCutScene, getStepsForSection, navigateToSection, exitTour]
  );

  const back = useCallback(
    (appConfig) => {
      if (showCutScene) {
        // Go back to previous section's last step
        const prevSec = sectionIndex - 1;
        if (prevSec < 0) return;
        const prevSteps = getStepsForSection(prevSec);
        setSectionIndex(prevSec);
        setShowCutScene(false);
        setStepIndex(Math.max(0, prevSteps.length - 1));
        navigateToSection(tourSections[prevSec], appConfig);
        return;
      }

      if (stepIndex > 0) {
        setStepIndex(stepIndex - 1);
        return;
      }

      // Move to previous section
      const prevSec = sectionIndex - 1;
      if (prevSec < 0) return;
      const prevSteps = getStepsForSection(prevSec);
      setSectionIndex(prevSec);
      if (prevSteps.length > 0) {
        setStepIndex(prevSteps.length - 1);
        setShowCutScene(false);
        navigateToSection(tourSections[prevSec], appConfig);
      } else if (tourSections[prevSec]?.cutScene) {
        setShowCutScene(true);
        setStepIndex(0);
      }
    },
    [sectionIndex, stepIndex, showCutScene, getStepsForSection, navigateToSection]
  );

  const pause = useCallback(() => setIsPaused(true), []);
  const resume = useCallback(() => setIsPaused(false), []);

  return (
    <EnterpriseTourContext.Provider
      value={{
        isActive,
        sectionIndex,
        stepIndex,
        isPaused,
        showCutScene,
        isNavigating,
        currentSection,
        currentStep,
        currentSteps,
        progress,
        totalSteps,
        completedSteps,
        actions: {
          startTour,
          exitTour,
          next,
          back,
          pause,
          resume,
          setNavigateFn,
        },
      }}
    >
      {children}
    </EnterpriseTourContext.Provider>
  );
};

export const useEnterpriseTour = () => {
  const ctx = useContext(EnterpriseTourContext);
  if (!ctx) throw new Error("useEnterpriseTour must be inside EnterpriseTourProvider");
  return ctx;
};

export default EnterpriseTourContext;
